import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Input, Label } from 'reactstrap';
import api from '../../../constants/api';

// Coordinates are never hardcoded here. City and Area come from the Area
// Management module and the pair is geocoded on selection, so this works for
// whichever country the backend locale config is set to. It previously held a
// static list of Abu Dhabi areas with baked-in lat/long values.
export default function Area({ onSelect, onLocationSelect, colSize, initialCity, initialArea }) {
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [cityId, setCityId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [error, setError] = useState('');

  const list = (response) => response?.data?.data || response?.data || [];
  const cityIdOf = (item) => item?.id ?? item?.city_id;
  const areaIdOf = (item) => item?.id ?? item?.area_id ?? item?.city_id;

  useEffect(() => {
    // Only active cities can be picked (same as Slot Booking); the API filters,
    // and the check here covers older API builds that ignore `status`.
    api
      .get('/servicearea/cities', { params: { status: 'active' } })
      .then((res) => {
        const loadedCities = list(res).filter((item) => Number(item.city_is_active ?? 1) === 1);
        setCities(loadedCities);
        const city = loadedCities.find((item) => item.city_name === initialCity);
        if (city) {
          const nextCityId = String(cityIdOf(city));
          setCityId(nextCityId);
          api
            .get(`/servicearea/cities/${nextCityId}/areas`)
            .then((areasResponse) => {
              const loadedAreas = list(areasResponse);
              setAreas(loadedAreas);
              const area = loadedAreas.find((item) => item.area_name === initialArea);
              if (area) setAreaId(String(areaIdOf(area)));
            })
            .catch(() => setError('Unable to load areas for the selected city.'));
        }
      })
      .catch(() => setError('Unable to load cities.'));
  }, [initialArea, initialCity]);

  const handleCity = (event) => {
    const nextCityId = event.target.value;
    setCityId(nextCityId);
    setAreaId('');
    setAreas([]);
    setError('');
    onSelect('');
    onLocationSelect({ city: '', area: '', latlong: '' });

    if (!nextCityId) return;

    api
      .get(`/servicearea/cities/${nextCityId}/areas`)
      .then((res) => setAreas(list(res)))
      .catch(() => setError('Unable to load areas for the selected city.'));
  };

  const handleArea = (event) => {
    const nextAreaId = event.target.value;
    const city = cities.find((item) => String(cityIdOf(item)) === String(cityId));
    const area = areas.find((item) => String(areaIdOf(item)) === String(nextAreaId));
    setAreaId(nextAreaId);
    setError('');
    onSelect('');
    onLocationSelect({
      city: city?.city_name || '',
      area: area?.area_name || '',
      latlong: '',
    });

    if (!city || !area) return;

    api
      .get('/location/geocode', { params: { city: city.city_name, area: area.area_name } })
      .then((res) => {
        const geocode = res?.data?.data;
        if (res?.data?.success === true && geocode?.lat !== undefined && geocode?.long !== undefined) {
          onSelect(`${geocode.lat},${geocode.long}`);
          onLocationSelect({
            city: city.city_name,
            area: area.area_name,
            latlong: `${geocode.lat},${geocode.long}`,
          });
          return;
        }
        setError('No coordinates found for the selected city and area.');
      })
      .catch((err) => {
        setError(
          err?.response?.status === 404
            ? 'No coordinates found for the selected city and area.'
            : 'Unable to get coordinates for the selected city and area.',
        );
      });
  };

  return (
    <>
      <Col lg={colSize} sm={12}>
        <Label for="area-city">City</Label>
        <Input type="select" id="area-city" className="form-control" value={cityId} onChange={handleCity}>
          <option value="">Select City</option>
          {cities.map((city) => (
            <option key={cityIdOf(city)} value={cityIdOf(city)}>
              {city.city_name}
            </option>
          ))}
        </Input>
      </Col>
      <Col lg={colSize} sm={12}>
        <Label for="area-area">Area</Label>
        <Input
          type="select"
          id="area-area"
          className="form-control"
          value={areaId}
          onChange={handleArea}
          disabled={!cityId}
        >
          <option value="">Select Area</option>
          {areas.map((area) => (
            <option key={areaIdOf(area)} value={areaIdOf(area)}>
              {area.area_name}
            </option>
          ))}
        </Input>
        {error && <small className="text-danger d-block mt-1">{error}</small>}
      </Col>
    </>
  );
}

Area.propTypes = {
  // Receives "lat,long" once a city+area pair resolves, or '' while incomplete.
  onSelect: PropTypes.func.isRequired,
  onLocationSelect: PropTypes.func,
  colSize: PropTypes.number,
  initialCity: PropTypes.string,
  initialArea: PropTypes.string,
};

Area.defaultProps = {
  colSize: 3,
  initialCity: '',
  initialArea: '',
  onLocationSelect: () => {},
};
